import { useFormContext } from "react-hook-form";
import { FormControlLabel, Radio } from "@mui/material";

import { SelectField } from "components/AddVaccineForm/components";
import { FormField } from "components/form/FormField/FormField";
import { isYesNoImmunocompromisedSet } from "features/immunizations/utils/riskFactorSelection";

import style from "../AddVaccineForm.module.css";

const FIELD_NAME = "immunocompromisedAssessment";

/**
 * "Patient Immunocompromised?" — Phase 2 §5.5.1-5.5.3.
 *
 * The question mark is part of the requirement, which asks for it as a standardised
 * naming convention, and the spec calls for radio buttons with no default selection:
 * an unanswered question and a No have opposite consequences on the wire, so the value
 * stays empty until the clinician chooses one.
 *
 * Which control appears follows the codes the server sends for the record's
 * specification version, not the version number itself — a set this component does not
 * recognise is still offered as a select rather than not at all.
 */
export const ImmunocompromisedField = ({ options }) => {
  const { watch, setValue, trigger } = useFormContext();
  const answer = watch(FIELD_NAME);

  if (!isYesNoImmunocompromisedSet(options)) {
    return (
      <SelectField
        name={FIELD_NAME}
        label="Patient Immunocompromised?"
        options={options}
        placeholder="Select"
      />
    );
  }

  return (
    <FormField name={FIELD_NAME}>
      <label className={style.pregnancyFormLabel}>
        Patient Immunocompromised? *
      </label>
      {/* Validated on the way out as well as on submit: the requirement calls out the
          clinician who clicks the field and moves on without answering. */}
      <div
        className={style.radioGroup}
        onBlur={() => trigger(FIELD_NAME)}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={
              <Radio
                size="small"
                checked={answer === option.value}
                inputProps={{
                  "data-testid": `immunocompromised-${option.label.toLowerCase()}`,
                }}
                onChange={() =>
                  setValue(FIELD_NAME, option.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                sx={{ "&.Mui-checked": { color: "var(--green-900)" } }}
              />
            }
            label={option.label}
          />
        ))}
      </div>
    </FormField>
  );
};
